import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';
import { COLORS } from './constants';
import CustomBar from './CustomBar';
import CustomTooltip from './CustomTooltip';
import ExportMenu from './../data/selected_data/export_menu/ExportMenu';

const OverviewView = ({ stats, synthesisMethods, substanceTypes, rawData, navigateToType, navigateToSubType }) => {

    const sortedSubstanceTypes = [...substanceTypes].sort((a, b) => b.value - a.value);
    const topSubstanceTypePercentage = stats.totalProducts > 0
        ? ((stats.topSubstanceTypeCount / stats.totalProducts) * 100).toFixed(1)
        : 0;

    const AllData = rawData || [];

    const renderStatsText = () => (
        <div className="stats-summary">
            <h3>Database Insights</h3>
            <p>
                The EMRD database contains <strong>{stats.totalProducts}</strong> medicines across <strong>{substanceTypes.length}</strong> substance types
                and <strong>{stats.totalSubTypes}</strong> subtypes. The most common substance type is <strong>{stats.topSubstanceType}</strong> with
                <strong> {stats.topSubstanceTypeCount}</strong> medicines ({topSubstanceTypePercentage}% of database).
            </p>
            <p>
                Distribution by synthesis method shows <strong>{stats.biologicallyCount}</strong> biologically synthesized medicines and
                <strong> {stats.chemicallyCount}</strong> chemically synthesized medicines.
            </p>
            {stats.topSubTypes && stats.topSubTypes.length > 0 && (
                <div>
                    <p>Top subtypes in the database:</p>
                    <ul className="top-subtypes-list">
                        {stats.topSubTypes.map((subType, index) => (
                            <li key={`${subType.type}-${subType.subType}-${index}`} className="top-subtype-item">
                                <span className="subtype-badge" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
                                <strong>{subType.subType}</strong> ({subType.type}) - {subType.count} medicines
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );

    const [selectedSubtypeKey, setSelectedSubtypeKey] = useState(null);

    const renderDetailedNavigation = () => {
        if (!substanceTypes || substanceTypes.length === 0) {
            return (
                <div className="detailed-navigation-section chart-container">
                    <div className="chart-header">
                        <h3>Explore All Categories</h3>
                    </div>
                    <p>No substance data available for detailed navigation.</p>
                </div>
            );
        }

        // Sort substance types by count (descending)
        const sortedSubstanceTypes = [...substanceTypes].sort((a, b) => b.value - b.value);

        const selectedType = sortedSubstanceTypes.find(type => type.name === selectedSubtypeKey);
        const hasOnlyOneSubtype = selectedType && selectedType.subTypes && Object.keys(selectedType.subTypes).length === 1;

        return (
            <div className="detailed-navigation-section chart-container">
                <div className="chart-header">
                    <h3>Explore All Categories</h3>
                </div>
                <p className="chart-description">
                    Click on a category to {sortedSubstanceTypes.some(type => type.subTypes && Object.keys(type.subTypes).length > 1) ? "view its subcategories below" : "see more details"}
                    {sortedSubstanceTypes.some(type => type.subTypes && Object.keys(type.subTypes).length > 1) ? ", or click the category name to see more details." : "."}
                </p>

                <div className="detailed-nav-types-container">
                    {sortedSubstanceTypes.map((type) => {
                        const subTypeCount = type.subTypes ? Object.keys(type.subTypes).length : 0;
                        const subcategoriesText = subTypeCount === 0 ? "No subcategories" : subTypeCount === 1 ? 'No further classification. Click to visit type page' : `${subTypeCount} Subcategories`;
                        return (
                            <div
                                key={type.name}
                                className={`detailed-nav-type ${selectedSubtypeKey === type.name ? 'selected' : ''}`}
                                onClick={(e) => {
                                    if (e.target.tagName === 'STRONG') {
                                        navigateToType(type.name);
                                    } else {
                                        setSelectedSubtypeKey(type.name);
                                    }
                                }}
                            >
                                <strong
                                    className="detailed-nav-type-name"
                                    style={{ cursor: 'pointer', color: '#1a3a5f' }}
                                >
                                    {type.name} ({type.value} Medicines)
                                </strong>
                                <p
                                    className="detailed-nav-subtype-count"
                                    style={{ cursor: subTypeCount === 1 ? 'pointer' : 'default' }}
                                    onClick={subTypeCount === 1 ? () => navigateToType(type.name) : undefined}
                                >
                                    {subcategoriesText}
                                </p>

                            </div>
                        );
                    })}
                </div>

                {selectedType && selectedType.subTypes && Object.keys(selectedType.subTypes).length > 0 && !hasOnlyOneSubtype && (
                    <div className="selected-subtypes-section">
                        <h3>
                            <span
                                onClick={() => navigateToType(selectedType.name)}
                                style={{ cursor: 'pointer', color: '#1a3a5f', textDecoration: 'underline' }}
                            >
                                View all {selectedType.name}
                            </span>
                        </h3>
                        <SubtypeList
                            subTypes={selectedType.subTypes}
                            typeName={selectedType.name}
                            navigateToSubType={navigateToSubType}
                        />
                    </div>
                )}
            </div>
        );
    };

    // Component for displaying subtypes in two columns
    const SubtypeList = ({ subTypes, typeName, navigateToSubType }) => {
        const sortedSubTypes = Object.entries(subTypes)
            .sort(([a], [b]) => a.localeCompare(b));

        const midIndex = Math.ceil(sortedSubTypes.length / 2);
        const columnOne = sortedSubTypes.slice(0, midIndex);
        const columnTwo = sortedSubTypes.slice(midIndex);

        return (
            <div style={{ display: 'flex' }}>
                <ul className="detailed-nav-subtype-list" style={{ width: '50%', paddingRight: '10px', boxSizing: 'border-box' }}>
                    {columnOne.map(([subTypeName, subTypeData]) => {
                        const medicineText = subTypeData.count === 1 ? 'Medicine' : 'Medicines';
                        return (
                            <li key={subTypeName} className="detailed-nav-subtype">
                                <span onClick={() => navigateToSubType(typeName, subTypeName)} style={{ cursor: 'pointer' }}>
                                    {subTypeName} ({subTypeData.count} {medicineText})
                                </span>
                            </li>
                        );
                    })}
                </ul>
                <ul className="detailed-nav-subtype-list" style={{ width: '50%', paddingLeft: '10px', boxSizing: 'border-box' }}>
                    {columnTwo.map(([subTypeName, subTypeData]) => {
                        const medicineText = subTypeData.count === 1 ? 'Medicine' : 'Medicines';
                        return (
                            <li key={subTypeName} className="detailed-nav-subtype">
                                <span onClick={() => navigateToSubType(typeName, subTypeName)} style={{ cursor: 'pointer' }}>
                                    {subTypeName} ({subTypeData.count} {medicineText})
                                </span>
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    };


    return (
        <div className="distribution-overview">
            <div className="page-header">
                <h2>Substance Type Explorer</h2>
                <p className="subtitle">Interactive visualisation of substance distribution across the EMRD database</p>
                <p className="intro-sentence">See how different types of medicines in our database are categorised and related based on their active substances.</p>
            </div>

            <div className="distribution-stats">
                <div className="stat-card">
                    <div className="stat-icon">📊</div>
                    <h3>{stats.totalProducts}</h3>
                    <p>Total Medicines</p>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">🧩</div>
                    <h3>{stats.totalSubTypes}</h3>
                    <p>Substance Subtypes</p>
                    <span className="sub-stat">Across {substanceTypes.length} types</span>
                </div>
                <div className="stat-card highlight-card">
                    <div className="stat-icon">🏆</div>
                    <h3>{stats.topSubstanceType}</h3>
                    <p>Top Substance Type</p>
                    <span className="sub-stat">{stats.topSubstanceTypeCount} medicines</span>
                </div>
            </div>

            {renderStatsText()}

            {/* Synthesis Method Distribution */}
            <div className="chart-container">
                <div className="chart-header">
                    <h3>Synthesis Method Distribution</h3>
                </div>
                {synthesisMethods.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                            data={synthesisMethods}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: 50, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" dataKey="total" />
                            <YAxis dataKey="name" type="category" width={100} />
                            <Tooltip formatter={(value, name) => [value, name === 'name' ? 'Synthesis Method' : name]} />
                            <Legend />
                            {substanceTypes.map((type, index) => ( // Added index for unique key for Bar
                                <Bar
                                    key={`synthesis-bar-${type.name}-${index}`}
                                    dataKey={type.name}
                                    name={type.name}
                                    stackId="a"
                                    fill={COLORS[substanceTypes.indexOf(type) % COLORS.length]}
                                />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="no-data"><p>No synthesis method data available.</p></div>
                )}
            </div>

            {/* Products by Substance Type */}
            <div className="chart-container interactive-chart">
                <div className="chart-header">
                    <h3>Medicines by Substance Type</h3>
                    <div className="chart-instruction">
                        <span className="pulse-dot"></span>
                        Click on any bar to explore subtypes
                    </div>
                    <div className="button-container">
                        <ExportMenu selectedData={AllData} text="Export All" />
                    </div>
                </div>
                {sortedSubstanceTypes.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                            data={sortedSubstanceTypes}
                            margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} height={100} />
                            <YAxis />
                            <Tooltip content={<CustomTooltip currentView="overview" />} />
                            {/* Ensure CustomBar receives navigateToType (or equivalent) and uses 'name' prop correctly */}
                            <Bar dataKey="value" shape={(props) => <CustomBar {...props} onClick={navigateToType} />}>
                                {sortedSubstanceTypes.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="no-data"><p>No substance type data available.</p></div>
                )}
            </div>

            {/* Detailed Navigation Section */}
            {renderDetailedNavigation()}

        </div>
    );
};

export default OverviewView;